import { MouseEvent, PropsWithChildren, RefObject, useRef, useState } from 'react';
import { Avatar, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, useMediaQuery, useTheme, Drawer, Skeleton } from '@mui/material';
import useEntraAccount from '@hooks/useEntraAccount';
import LogOutIcon from '@mui/icons-material/LogoutTwoTone';
import { useFetchMyProfileQuery } from '@api/Trinium/Endpoints/myProfile';
import { ContactSupportMenuItem } from './NavbarItems/ContactSupport';
import { RemoteAssistanceMenuItem } from './NavbarItems/RemoteAssistance';
import TerminalPreferencesMenuItem from './NavbarItems/TerminalPreferences';

import styles from './UserAvatar.module.css';

/**
 * Avatar display for the currently logged in User containing popover menu with actions regarding the user. If the user code contains 3+ characters than
 * the first 3 characters are used for the Avatar. Otherwise, two characters are used. Clicking the avatar opens the Account Menu.
 */
export default function UserAvatar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { logout } = useEntraAccount();
  const { data: profileData, isLoading: profileLoading } = useFetchMyProfileQuery(undefined);
  const anchorRef = useRef(null);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  if (profileLoading || !profileData)
    return (
      <Skeleton
        variant='circular'
        className={styles['avatar-btn']}
      />
    );

  return (
    <>
      <IconButton
        className={styles['avatar-btn']}
        onClick={toggleMenu}
        title='Account Settings'
        ref={anchorRef}>
        <Avatar className={`${styles.avatar} ${profileData.userCode.length > 2 ? styles['three-char'] : ''}`}>
          {profileData.userCode.length > 2 ? profileData.userCode.substring(0, 3) : profileData.userCode}
        </Avatar>
      </IconButton>

      <UserAvatarMenu
        isOpen={menuOpen}
        toggleMenu={toggleMenu}
        anchorRef={anchorRef}>
        <TerminalPreferencesMenuItem
          className={styles.item}
          textClassName={styles.text}
          onClick={toggleMenu}
        />
        <RemoteAssistanceMenuItem
          key='account-menu-remote-assistance'
          className={styles.item}
          textClassName={styles.text}
          onClick={toggleMenu}
        />
        <ContactSupportMenuItem
          key='account-menu-contact-support'
          className={styles.item}
          textClassName={styles.text}
          onClick={toggleMenu}
        />
        <MenuItem
          key='account-menu-logout'
          className={styles.item}
          onClick={() => {
            logout();
          }}>
          <ListItemIcon>
            <LogOutIcon />
          </ListItemIcon>
          <ListItemText
            slotProps={{
              primary: {
                className: styles.text
              }
            }}>
            Sign out
          </ListItemText>
        </MenuItem>
      </UserAvatarMenu>
    </>
  );
}

type UserAvatarMenuProps = PropsWithChildren & {
  isOpen: boolean;
  toggleMenu: (event: MouseEvent<HTMLElement>) => void;
  anchorRef: RefObject<HTMLElement | null>;
};

function UserAvatarMenu(props: UserAvatarMenuProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const appBar = document.getElementsByTagName('header');
  const appBarBottom = appBar.length > 0 ? appBar[0].getBoundingClientRect().bottom : 48;

  if (isMobile) {
    return (
      <Drawer
        anchor='top'
        open={props.isOpen}
        onClose={props.toggleMenu}
        transitionDuration={300}>
        <div
          style={{ marginTop: `${String(appBarBottom)}px` }}
          role='presentation'>
          {props.children}
        </div>
      </Drawer>
    );
  }

  return (
    <Menu
      slotProps={{
        paper: {
          sx: {
            // Need to specify important to override the mui element styles
            top: `${String(appBarBottom)}px !important`
          }
        },
        list: {
          className: styles.list,
          dense: true
        }
      }}
      anchorEl={props.anchorRef.current}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      keepMounted
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      open={props.isOpen}
      onClose={props.toggleMenu}>
      {props.children}
    </Menu>
  );
}
